/**
 * Página /vecino/documentos — Sprint 11 v2 S11-V2-09, refactor S12-09.
 *
 * Vista dedicada al listado completo de documentos internos del barrio
 * publicados por la comisión. Consume `GET /vecino/documentos/` que
 * retorna los vigentes a la fecha.
 *
 * Sprint 12 S12-09 reemplazó el mock estático por fetch real.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { listarDocumentosVecino } from '../../lib/api/documentos';
import type { DocumentoRead } from '../../lib/api/types';
import { DocCard } from '../../components/ui/DocCard';
import { EmptyState } from '../../components/ui/EmptyState';
import { InfoNote } from '../../components/ui/InfoNote';
import { DOC_ICONS_BY_VARIANT } from '../../data/doc-icons';
import {
  iconVariantPorTipo,
  metaDocumento,
} from '../../lib/dashboard-helpers';

export function Documentos() {
  const [documentos, setDocumentos] = useState<DocumentoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listarDocumentosVecino()
      .then((data) => {
        if (!cancelado) setDocumentos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `No pudimos cargar los documentos (error ${err.status}).`
            : 'No pudimos cargar los documentos. Intenta recargar la página.',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return (
    <div>
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-5">
        <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
          Documentos internos
        </div>
        <h1 className="text-2xl font-medium text-text-primary leading-tight">
          Documentos del barrio
        </h1>
        <p className="text-base text-text-secondary mt-1">
          Protocolos, actas, informes y otros archivos publicados por la
          Comisión.
        </p>
      </div>

      <div className="px-4 md:px-8 py-6">
        {loading && (
          <p className="text-sm text-text-muted">Cargando documentos…</p>
        )}
        {error && !loading && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
        {!loading && !error && documentos.length === 0 && (
          <div className="max-w-3xl">
            <EmptyState
              title="La Comisión aún no ha publicado documentos"
              description="Cuando se publiquen actas, reglamentos u otros archivos del barrio, aparecerán aquí."
            />
          </div>
        )}
        {!loading && !error && documentos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
            {documentos.map((doc) => {
              const variant = iconVariantPorTipo(doc.tipo);
              return (
                <a
                  key={doc.id}
                  href={doc.url_externa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90 transition-opacity"
                  aria-label={`Abrir ${doc.titulo}`}
                >
                  <DocCard
                    icon={DOC_ICONS_BY_VARIANT[variant]!}
                    iconVariant={variant}
                    title={doc.titulo}
                    meta={metaDocumento(doc)}
                  />
                </a>
              );
            })}
          </div>
        )}

        {!loading && !error && (
          <div className="max-w-3xl mt-6">
            <InfoNote>
              Los documentos abren en una nueva pestaña. La Comisión los
              publica en almacenamiento externo (Google Drive u otro) y este
              portal solo muestra los enlaces vigentes.
            </InfoNote>
          </div>
        )}
      </div>
    </div>
  );
}
